<template id="input">
    <div
        class="field-wrapper field-wrapper--floating-label"
        :class="classList"
        v-if="inputConfig.type !== 'hidden'">
        <input
            v-model="value"
            :id="inputConfig.id || inputConfig.name"
            :name="inputConfig.name"
            :class="inputConfig.styleClass"
            :placeholder="inputConfig.placeholderMsg"
            :type="getInputType"
            :autocomplete="inputConfig.autoComplete"
            :maxlength="inputConfig.maxlength"
            :tabindex="inputConfig.tabIndex"
            :ref="inputConfig.ref"
            :data-recurly="inputConfig.dataRecurly"
            :readonly="inputConfig.readonly"
            :required="isRequired()"
            :aria-describedby="inputConfig.ariaDescribedBy"
            :aria-required="isRequired()"
            @input="updateValue()"
            @focus="handleFocus"
            @blur="handleBlur"
            @keyup.enter="handleKeyUp"
            :data-ci="inputConfig.name" />
        <label :class="{ floated: hasValue }" :for="inputConfig.id || inputConfig.name">
            {{ inputConfig.label }}
        </label>
        <div
            class="showhide-password"
            v-if="enableShowHide"
            @click="toggleShowHide"
            @keypress.enter.space.prevent="toggleShowHide">
            <span :class="getShowHideClass" tabindex="0"></span>
        </div>
        <div v-if="inputConfig.iconClass" class="icon" :class="inputConfig.iconClass">
            <TooltipOverlay v-if="inputConfig.tooltip.id" :id="inputConfig.tooltip.id">
                <div v-if="inputConfig.tooltip.content" v-html="inputConfig.tooltip.content"></div>
            </TooltipOverlay>
        </div>
    </div>
</template>

<script>
    import { SHOWHIDE_PASSWORD } from 'helpers/featureConstants';
    import { mapGetters } from 'vuex';
    import TooltipOverlay from 'atoms/TooltipOverlay';
    import FormInputConfig from 'models/FormInputConfig';

    export default {
        components: {
            TooltipOverlay,
        },
        props: {
            inputConfig: {
                type: Object,
                default: FormInputConfig,
                required: true,
            },
            elementIndex: {
                type: Number,
                required: false,
                default: -1,
            },
            errorMessage: {
                type: String,
                required: false,
                default: '',
            },
            disableAutofocus: {
                type: Boolean,
                default: false,
            },
        },
        data: function () {
            return {
                focused: false,
                name: this.inputConfig.name,
                value: this.inputConfig.value,
                useHideIcon: false,
                isPasswordField: false,
                resetOnFocusRequired: true,
            };
        },

        created: function () {
            this.$emit('input', {
                name: this.name,
                value: this.inputConfig.value,
                type: this.inputConfig.type,
            });
            this.isPasswordField = this.inputConfig.type === 'password';
        },
        mounted() {
            let defaultInputFocus = 0;
            // START TEST PLOCTOPLUS-2359: A/B Test: Center Align & Field Hierarchy on Create Account Page
            if (
                this.$route?.name === 'SIGNUP' &&
                (this.twoColumnFieldAlignmentEmailFirst || this.centerAlignFieldEmailAndPassFirst)
            ) {
                defaultInputFocus = 1;
            }
            // END TEST PLOCTOPLUS-2359: A/B Test: Center Align & Field Hierarchy on Create Account Page
            this.$nextTick(() => {
                if (!this.disableAutofocus) {
                    if (
                        this.$refs[this.inputConfig.ref] &&
                        this.elementIndex === defaultInputFocus
                    ) {
                        this.$refs[this.inputConfig.ref].focus();
                    }
                }
            });
        },

        methods: {
            updateValue() {
                this.$emit('input', {
                    name: this.name,
                    value: this.$refs[this.inputConfig.ref].value,
                    type: this.inputConfig.type,
                });
            },

            isRequired() {
                if (
                    this.inputConfig.validation &&
                    this.inputConfig.validation.includes('required')
                ) {
                    return true;
                }
                return false;
            },

            handleFocus() {
                this.focused = true;
                let computedData = {
                    name: this.name,
                    value: this.$refs[this.inputConfig.ref].value,
                };
                if (this.resetOnFocusRequired) {
                    computedData = {
                        ...computedData,
                        resetOnFocusRequired: this.resetOnFocusRequired,
                    };
                    this.resetOnFocusRequired = false;
                }
                this.triggerValidation('focus');
                this.$emit('focus', computedData);
            },

            handleBlur() {
                this.focused = false;

                if (this.$refs[this.inputConfig.ref]) {
                    let value = this.$refs[this.inputConfig.ref].value;

                    this.$emit('blur', {
                        value,
                    });

                    if (value) {
                        this.triggerValidation('blur');
                    }
                } else {
                    this.$emit('blur');
                }
            },
            handleKeyUp(e) {
                this.$emit('keyup', {
                    event: e,
                    name: this.name,
                    value: this.value,
                });
                this.triggerValidation('keyup');
            },

            triggerValidation(event) {
                this.$emit('runValidator', {
                    on: event,
                    name: this.name,
                    value: this.value,
                    // keyValue: this.keyValue
                });
            },

            toggleShowHide() {
                this.useHideIcon = !this.useHideIcon;
                this.$emit('showHidePasswordStatus', this.useHideIcon);
            },
        },

        computed: {
            getInputType() {
                if (this.enableShowHide) {
                    if (this.useHideIcon) {
                        return 'text';
                    } else {
                        return 'password';
                    }
                }
                return this.inputConfig.type;
            },
            ...mapGetters(['featureIsActive']),
            // START TEST PLOCTOPLUS-2359: A/B Test: Center Align & Field Hierarchy on Create Account Page
            ...mapGetters({
                twoColumnFieldAlignmentEmailFirst: ['variant/twoColumnFieldAlignmentEmailFirst'],
                centerAlignFieldEmailAndPassFirst: ['variant/centerAlignFieldEmailAndPassFirst'],
            }),
            // START TEST PLOCTOPLUS-2359: A/B Test: Center Align & Field Hierarchy on Create Account Page
            enableShowHide() {
                return (
                    this.isPasswordField && this.$store.getters.featureIsActive(SHOWHIDE_PASSWORD)
                );
            },
            hasValue: function () {
                return (this.value && this.value !== '') || this.inputConfig.placeholderMsg;
            },
            classList: function () {
                return [
                    this.inputConfig.id ? 'field-wrapper--' + this.inputConfig.id : '',
                    this.focused ? 'focused' : '',
                    this.errorMessage ? 'error' : '',
                ];
            },
            getShowHideClass() {
                if (this.enableShowHide) {
                    return ['showhide', this.useHideIcon ? 'hide-icon' : 'show-icon'];
                }
            },
        },
    };
</script>
